import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, HelpCircle, ArrowRight, Shield, ArrowLeft } from 'lucide-react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { RiskLevelBadge } from '../components/RiskLevelBadge';
import { RiskLevel } from '../types';
import { SEO } from '../components/SEO';
import { scamAssessments } from '../data/scamAssessments';
export function CheckScamPage() {
  const {
    language,
    t
  } = useLanguage();
  const {
    type
  } = useParams();
  const [riskLevel, setRiskLevel] = useState<RiskLevel | null>(null);
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [progress, setProgress] = useState(0);
  // Get specific assessment if type exists, otherwise use default
  const specificAssessment = type ? scamAssessments.find(a => a.scamType === type) : null;
  const defaultQuestions = [{
    id: 'urgency',
    text: {
      en: t('redFlag.immediateAction')
    },
    weight: 1
  }, {
    id: 'money',
    text: {
      en: t('checkScam.askingMoney')
    },
    weight: 1
  }, {
    id: 'secrecy',
    text: {
      en: t('redFlag.secrecy')
    },
    weight: 1
  }, {
    id: 'unsolicited',
    text: {
      en: t('redFlag.unsolicited')
    },
    weight: 1
  }, {
    id: 'tooGood',
    text: {
      en: t('redFlag.tooGood')
    },
    weight: 1
  }, {
    id: 'personalInfo',
    text: {
      en: t('redFlag.personalInfo')
    },
    weight: 1
  }, {
    id: 'unusualPayment',
    text: {
      en: t('redFlag.unusualPayment')
    },
    weight: 1
  }, {
    id: 'threats',
    text: {
      en: t('redFlag.threats')
    },
    weight: 1
  }];
  const questions = specificAssessment ? specificAssessment.questions : defaultQuestions;
  const pageTitle = specificAssessment ? specificAssessment.title[language] || specificAssessment.title['en'] : t('nav.checkScam');
  const pageDescription = specificAssessment ? specificAssessment.description[language] || specificAssessment.description['en'] : t('seo.checkScam.desc');
  useEffect(() => {
    const answeredCount = Object.keys(answers).length;
    const progressPercent = answeredCount / questions.length * 100;
    setProgress(progressPercent);
  }, [answers, questions.length]);
  const calculateRisk = () => {
    const totalAnswered = Object.keys(answers).length;
    if (totalAnswered < questions.length) {
      alert(t('checkScam.answerAllQuestions'));
      return;
    }
    // Calculate score based on weights
    let score = 0;
    let maxScore = 0;
    questions.forEach(q => {
      const weight = q.weight || 1;
      maxScore += weight;
      if (answers[q.id]) {
        score += weight;
      }
    });
    const percentage = score / maxScore * 100;
    if (percentage >= 60) setRiskLevel('High');else if (percentage >= 30) setRiskLevel('Medium');else setRiskLevel('Low');
  };
  const handleReset = () => {
    setRiskLevel(null);
    setAnswers({});
    setProgress(0);
  };
  const handleCheckboxChange = (id: string, value: boolean) => {
    setAnswers({
      ...answers,
      [id]: value
    });
  };
  return <div className="container mx-auto px-4 pt-24 pb-12 sm:pb-16 max-w-4xl" data-id="element-394">
      <SEO title={pageTitle || t('nav.checkScam')} description={pageDescription || ''} data-id="element-395" />

      {type && <div className="mb-6" data-id="element-396">
          <Link to={`/${language}/protocols/${type}`} className="inline-flex items-center text-sm text-slate-600 hover:text-primary transition-colors" data-id="element-397">
            <ArrowLeft className="w-4 h-4 mr-1" data-id="element-398" />
            {t('protocol.backToHome')}
          </Link>
        </div>}

      <div className="text-center mb-8 sm:mb-12 animate-fade-in" data-id="element-399">
        <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-info/10 rounded-2xl mb-4 sm:mb-6" data-id="element-400">
          <Shield className="h-7 w-7 sm:h-8 sm:w-8 text-info" data-id="element-401" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4 px-4" data-id="element-402">
          {pageTitle}
        </h1>
        <p className="text-base sm:text-xl text-muted-foreground max-w-2xl mx-auto px-4" data-id="element-403">
          {pageDescription}
        </p>

        {/* Switch to Chat Mode */}
        {type && <div className="mt-6" data-id="element-404">
            <Link to={`/${language}/chat-assessment/${type}`} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium transition-colors" data-id="element-405">
              💬 {t('checkScam.chatAssessment')}
            </Link>
          </div>}
      </div>

      {!riskLevel ? <>
          {/* Progress Bar */}
          <div className="mb-6 sm:mb-8" data-id="element-406">
            <div className="flex items-center justify-between mb-3" data-id="element-407">
              <span className="text-xs sm:text-sm font-medium text-gray-700" data-id="element-408">
                {t('checkScam.progress')}: {Object.keys(answers).length}{' '}
                {t('checkScam.of')} {questions.length} {t('checkScam.answered')}
              </span>
              <span className="text-xs sm:text-sm font-medium text-primary" data-id="element-409">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="w-full h-2.5 sm:h-3 bg-gray-200 rounded-full overflow-hidden" data-id="element-410">
              <div className="h-full bg-gradient-to-r from-info to-primary transition-all duration-500 ease-out rounded-full" style={{
            width: `${progress}%`
          }} data-id="element-411" />
            </div>
          </div>

          <Card className="p-4 sm:p-6 md:p-8" data-id="element-412">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center" data-id="element-413">
              <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs sm:text-sm mr-2 sm:mr-3 flex-shrink-0" data-id="element-414">
                ?
              </span>
              <span data-id="element-415">
                {type ? t('checkScam.specificIndicators') : t('checkScam.redFlagChecklist')}
              </span>
            </h2>

            <div className="space-y-3 sm:space-y-4" data-id="element-416">
              {questions.map((q, index) => <div key={q.id} className={`group p-3 sm:p-5 rounded-xl border-2 transition-all duration-200 ${answers[q.id] === true ? 'border-destructive/30 bg-destructive/5' : answers[q.id] === false ? 'border-success/30 bg-success/5' : 'border-gray-200 hover:border-gray-300 bg-white'}`} data-id="element-417">
                  <div className="flex items-start space-x-3 sm:space-x-4" data-id="element-418">
                    <span className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs sm:text-sm font-semibold mt-0.5" data-id="element-419">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0" data-id="element-420">
                      <label className="block text-sm sm:text-base font-medium text-gray-900 mb-2 sm:mb-3 cursor-pointer" data-id="element-421">
                        {q.text[language] || q.text['en']}
                      </label>
                      <div className="flex space-x-2 sm:space-x-3" data-id="element-422">
                        <button onClick={() => handleCheckboxChange(q.id, true)} className={`flex-1 px-3 sm:px-4 py-3 sm:py-3.5 rounded-lg border-2 font-medium text-xs sm:text-sm transition-all touch-manipulation active:scale-95 ${answers[q.id] === true ? 'border-destructive bg-destructive text-white shadow-sm' : 'border-gray-200 bg-white text-gray-700 hover:border-destructive/50 hover:bg-destructive/5'}`} data-id="element-423">
                          {t('checkScam.yes')}
                        </button>
                        <button onClick={() => handleCheckboxChange(q.id, false)} className={`flex-1 px-3 sm:px-4 py-3 sm:py-3.5 rounded-lg border-2 font-medium text-xs sm:text-sm transition-all touch-manipulation active:scale-95 ${answers[q.id] === false ? 'border-success bg-success text-white shadow-sm' : 'border-gray-200 bg-white text-gray-700 hover:border-success/50 hover:bg-success/5'}`} data-id="element-424">
                          {t('checkScam.no')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>

            <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t" data-id="element-425">
              <Button onClick={calculateRisk} fullWidth size="lg" disabled={Object.keys(answers).length < questions.length} className="relative" data-id="element-426">
                {Object.keys(answers).length < questions.length ? <>
                    <span className="opacity-50" data-id="element-427">
                      {t('protocol.checkRiskLevel')}
                    </span>
                    <span className="ml-2 text-xs" data-id="element-428">
                      ({questions.length - Object.keys(answers).length}{' '}
                      {t('checkScam.questionsRemaining')})
                    </span>
                  </> : <>
                    {t('protocol.checkRiskLevel')}
                    <ArrowRight className="ml-2 h-5 w-5" data-id="element-429" />
                  </>}
              </Button>
            </div>
          </Card>
        </> : <div className="space-y-6 sm:space-y-8 animate-fade-in" data-id="element-430">
          {/* Risk Level Status Bar */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 sm:p-8 border-2 border-gray-200" data-id="element-431">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-432">
              <h2 className="text-xl sm:text-2xl font-bold" data-id="element-433">
                {t('checkScam.assessmentComplete')}
              </h2>
              <RiskLevelBadge level={riskLevel} data-id="element-434" />
            </div>

            {/* Visual Risk Meter */}
            <div className="mb-6" data-id="element-435">
              <div className="flex items-center justify-between mb-2" data-id="element-436">
                <span className="text-xs sm:text-sm font-medium text-gray-600" data-id="element-437">
                  {t('checkScam.riskMeter')}
                </span>
                <span className="text-xs sm:text-sm font-medium text-gray-900" data-id="element-438">
                  {Object.values(answers).filter(Boolean).length} /{' '}
                  {questions.length} {t('checkScam.redFlags')}
                </span>
              </div>
              <div className="relative w-full h-3 sm:h-4 bg-gray-200 rounded-full overflow-hidden" data-id="element-439">
                <div className={`h-full transition-all duration-700 ease-out ${riskLevel === 'High' ? 'bg-destructive' : riskLevel === 'Medium' ? 'bg-warning' : 'bg-success'}`} style={{
              width: `${Object.values(answers).filter(Boolean).length / questions.length * 100}%`
            }} data-id="element-440" />
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500" data-id="element-441">
                <span data-id="element-442">{t('checkScam.lowRisk')}</span>
                <span data-id="element-443">{t('checkScam.mediumRisk')}</span>
                <span data-id="element-444">{t('checkScam.highRisk')}</span>
              </div>
            </div>
          </div>

          <Card className={`p-6 sm:p-8 border-l-4 sm:border-l-8 ${riskLevel === 'High' ? 'border-l-destructive bg-red-50' : riskLevel === 'Medium' ? 'border-l-warning bg-yellow-50' : 'border-l-success bg-green-50'}`} data-id="element-445">
            <div className="mb-6" data-id="element-446">
              {riskLevel === 'High' && <div className="flex items-start space-x-3 sm:space-x-4 text-destructive" data-id="element-447">
                  <AlertTriangle className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0" data-id="element-448" />
                  <div data-id="element-449">
                    <p className="text-lg sm:text-xl font-bold mb-2" data-id="element-450">
                      {t('checkScam.highProbability')}
                    </p>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed" data-id="element-451">
                      {t('checkScam.highProbabilityDesc')}
                    </p>
                  </div>
                </div>}
              {riskLevel === 'Medium' && <div className="flex items-start space-x-3 sm:space-x-4 text-warning" data-id="element-452">
                  <HelpCircle className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0" data-id="element-453" />
                  <div data-id="element-454">
                    <p className="text-lg sm:text-xl font-bold mb-2" data-id="element-455">
                      {t('checkScam.proceedCaution')}
                    </p>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed" data-id="element-456">
                      {t('checkScam.proceedCautionDesc')}
                    </p>
                  </div>
                </div>}
              {riskLevel === 'Low' && <div className="flex items-start space-x-3 sm:space-x-4 text-success" data-id="element-457">
                  <CheckCircle className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0" data-id="element-458" />
                  <div data-id="element-459">
                    <p className="text-lg sm:text-xl font-bold mb-2" data-id="element-460">
                      {t('checkScam.lowRiskDetected')}
                    </p>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed" data-id="element-461">
                      {t('checkScam.lowRiskDetectedDesc')}
                    </p>
                  </div>
                </div>}
            </div>

            {/* Recommendations */}
            <div className="bg-white rounded-xl p-4 sm:p-6 mb-6" data-id="element-462">
              <h3 className="font-bold text-base sm:text-lg mb-3 sm:mb-4" data-id="element-463">
                {t('checkScam.recommendedActions')}
              </h3>
              <ul className="space-y-2 sm:space-y-3" data-id="element-464">
                {riskLevel === 'High' && <>
                    <li className="flex items-start" data-id="element-465">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center text-xs font-bold mr-3 mt-0.5" data-id="element-466">
                        1
                      </span>
                      <span className="text-gray-700" data-id="element-467">
                        {t('emergency.step4.item1')}
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-468">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center text-xs font-bold mr-3 mt-0.5" data-id="element-469">
                        2
                      </span>
                      <span className="text-gray-700" data-id="element-470">
                        {t('emergency.step1.item1')}
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-471">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center text-xs font-bold mr-3 mt-0.5" data-id="element-472">
                        3
                      </span>
                      <span className="text-gray-700" data-id="element-473">
                        {t('action.startProtocol')}
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-474">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center text-xs font-bold mr-3 mt-0.5" data-id="element-475">
                        4
                      </span>
                      <span className="text-gray-700" data-id="element-476">
                        {t('emergency.reportToAuthorities')}
                      </span>
                    </li>
                  </>}
                {riskLevel === 'Medium' && <>
                    <li className="flex items-start" data-id="element-477">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-warning text-white flex items-center justify-center text-xs font-bold mr-3 mt-0.5" data-id="element-478">
                        1
                      </span>
                      <span className="text-gray-700" data-id="element-479">
                        {t('checkScam.verifyIdentity')}
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-480">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-warning text-white flex items-center justify-center text-xs font-bold mr-3 mt-0.5" data-id="element-481">
                        2
                      </span>
                      <span className="text-gray-700" data-id="element-482">
                        {t('checkScam.dontShareInfo')}
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-483">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-warning text-white flex items-center justify-center text-xs font-bold mr-3 mt-0.5" data-id="element-484">
                        3
                      </span>
                      <span className="text-gray-700" data-id="element-485">
                        {t('checkScam.researchOffer')}
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-486">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-warning text-white flex items-center justify-center text-xs font-bold mr-3 mt-0.5" data-id="element-487">
                        4
                      </span>
                      <span className="text-gray-700" data-id="element-488">
                        {t('checkScam.consultOthers')}
                      </span>
                    </li>
                  </>}
                {riskLevel === 'Low' && <>
                    <li className="flex items-start" data-id="element-489">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-success text-white flex items-center justify-center text-xs font-bold mr-3 mt-0.5" data-id="element-490">
                        1
                      </span>
                      <span className="text-gray-700" data-id="element-491">
                        {t('checkScam.monitorChanges')}
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-492">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-success text-white flex items-center justify-center text-xs font-bold mr-3 mt-0.5" data-id="element-493">
                        2
                      </span>
                      <span className="text-gray-700" data-id="element-494">
                        {t('warning.global')}
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-495">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-success text-white flex items-center justify-center text-xs font-bold mr-3 mt-0.5" data-id="element-496">
                        3
                      </span>
                      <span className="text-gray-700" data-id="element-497">
                        {t('checkScam.keepRecords')}
                      </span>
                    </li>
                  </>}
              </ul>
            </div>

            <div className="space-y-3" data-id="element-498">
              {riskLevel === 'High' && <Button href={`/${language}/emergency-protocol`} variant="destructive" fullWidth size="lg" data-id="element-499">
                  <AlertTriangle className="mr-2 h-5 w-5" data-id="element-500" />
                  {t('nav.emergency')}
                </Button>}
              <Button href={`/${language}/where-to-report`} variant="outline" fullWidth data-id="element-501">
                {t('protocol.findReportingChannels')}
              </Button>
              <Button onClick={handleReset} variant="ghost" fullWidth data-id="element-502">
                {t('checkScam.startOver')}
              </Button>
            </div>
          </Card>
        </div>}
    </div>;
}